import React, { useMemo, useState } from 'react';
import { X, Zap } from 'lucide-react';
import { toast } from 'react-toastify';
import {
  createProductJourney, setCampaignAudience,
  AudienceScope, DripAnchorEvent, DripCampaign, JourneyTrigger,
} from '@/actions/drip-campaigns';
import AudiencePicker, { TreeCat, allVariants } from './AudiencePicker';

interface Props {
  tree: TreeCat[];
  triggers: JourneyTrigger[];
  onClose: () => void;
  /** Called with the created journey so the caller can open the flow builder. */
  onCreated: (journey: DripCampaign) => void;
}

const CreateJourneyModal: React.FC<Props> = ({ tree, triggers, onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [anchor, setAnchor] = useState<DripAnchorEvent>('order_delivered');
  const [scope, setScope] = useState<AudienceScope>('product');
  const [productId, setProductId] = useState<number | null>(null);
  const [productIds, setProductIds] = useState<Set<number>>(new Set());
  const [categoryIds, setCategoryIds] = useState<Set<number>>(new Set());
  const [variantIds, setVariantIds] = useState<Set<number>>(new Set());
  const [creating, setCreating] = useState(false);

  const products = useMemo(() => tree.flatMap(c => c.products), [tree]);

  // Suggest a name from the audience so the field is rarely typed by hand, but never
  // overwrite something the user has already written.
  const suggestedName = useMemo(() => {
    if (scope === 'all') return 'All products — Post-purchase journey';
    if (scope === 'category') {
      const names = tree.filter(c => categoryIds.has(c.cat_id)).map(c => c.cat);
      return names.length ? `${names.join(', ')} — Post-purchase journey` : '';
    }
    if (scope === 'variants') {
      if (!variantIds.size) return '';
      const chosen = allVariants(tree).filter(v => variantIds.has(v.variant_id));
      // One pack size names itself — "Ashwagandha Probiotic 180 Capsules — …" — which
      // is the whole point of a SKU journey and beats "1 SKU".
      if (chosen.length === 1) {
        const v = chosen[0];
        return `${v.product_name} ${v.name || v.sku || ''} — Post-purchase journey`.replace('  ', ' ');
      }
      return `${chosen.length} SKUs — Post-purchase journey`;
    }
    if (scope === 'products') return productIds.size
      ? `${productIds.size} products — Post-purchase journey` : '';
    const p = products.find(x => x.product_id === productId);
    return p ? `${p.name} — Post-purchase journey` : '';
  }, [scope, categoryIds, productIds, variantIds, productId, tree, products]);

  const effectiveName = name.trim() || suggestedName;

  const missing =
    !effectiveName ||
    (scope === 'product' && !productId) ||
    (scope === 'products' && productIds.size === 0) ||
    (scope === 'category' && categoryIds.size === 0) ||
    (scope === 'variants' && variantIds.size === 0);

  const create = async () => {
    if (missing) return;
    setCreating(true);
    try {
      const res = await createProductJourney({
        campaign_name: effectiveName,
        anchor_event: anchor,
        // Only meaningful for the 'product' scope; a category or all-products journey
        // has no single product and must not pretend to.
        product_id_fk: scope === 'product' ? productId : null,
      });
      if (!res?.success || !res.data) {
        toast.error('Could not create the journey');
        return;
      }
      const journey = res.data as DripCampaign;

      const targets = scope === 'variants'
        ? Array.from(variantIds).map(variant_id => ({ variant_id }))
        : scope === 'products'
          ? Array.from(productIds).map(product_id => ({ product_id }))
          : scope === 'category'
            ? Array.from(categoryIds).map(category_id => ({ category_id }))
            : [];
      // 'product' is the server-side default, so only the other scopes need writing.
      if (scope !== 'product') {
        await setCampaignAudience(journey.id, scope, targets);
      }

      toast.success('Journey created — now add your steps');
      onCreated(journey);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Could not create the journey');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-start justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700">
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">
              New journey
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              What starts it, and who it is for. Steps come next.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto space-y-5">
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Name
            </label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={suggestedName || 'e.g. Gut Health — Post-purchase journey'}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-900 dark:text-white focus:ring-2 focus:ring-brandColor focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              What starts this journey?
            </label>
            <select
              value={anchor}
              onChange={(e) => setAnchor(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-900 dark:text-white focus:ring-2 focus:ring-brandColor focus:border-transparent"
            >
              {triggers.length === 0 && <option value="order_delivered">Order delivered</option>}
              {triggers.map(t => (
                <option key={t.key} value={t.key}>{t.label}</option>
              ))}
            </select>
            {triggers.find(t => t.key === anchor)?.description && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-start gap-1">
                <Zap className="w-3 h-3 mt-0.5 flex-shrink-0" />
                {triggers.find(t => t.key === anchor)?.description}
              </p>
            )}
            {/* The list is deliberately short — explain that rather than leaving it
                looking like the other triggers were forgotten. */}
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              Only triggers the engine can act on are listed. Others (cart abandoned,
              reorder due, NDR, RTO) are designed but not wired up yet.
            </p>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
              Who is it for?
            </label>
            <AudiencePicker
              tree={tree}
              scope={scope} onScopeChange={setScope}
              productId={productId} onProductIdChange={setProductId}
              productIds={productIds} onProductIdsChange={setProductIds}
              categoryIds={categoryIds} onCategoryIdsChange={setCategoryIds}
              variantIds={variantIds} onVariantIdsChange={setVariantIds}
            />
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 px-5 py-4 border-t border-gray-100 dark:border-gray-700">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Created as a draft in test mode — nothing reaches a customer until you activate it.
          </p>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              onClick={create}
              disabled={creating || missing}
              className="px-4 py-2 text-sm rounded-lg bg-brandColor text-white hover:opacity-90 disabled:opacity-60"
            >
              {creating ? 'Creating…' : 'Create & add steps'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateJourneyModal;
